import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { QuoteBoardRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

const TABS = [
  ['live', 'Open'],
  ['accepted', 'Accepted'],
  ['cancelled', 'Cancelled'],
  ['all', 'All'],
] as const;

/**
 * The prescription board, for oversight.
 *
 * ── THE QUESTION THIS SCREEN ANSWERS ────────────────────────────────────────
 *
 * Is the board working? Before this, nobody at ChiCare could tell. A buyer
 * photographs a prescription, it goes to five pharmacies, and then — as far as
 * anybody here could see — nothing. Whether they replied, whether the prices
 * were sane, whether requests were quietly expiring unanswered was invisible.
 *
 * Each number on it is a decision:
 *
 *   unanswered   recruit sellers in that area, or widen the fan-out
 *   expired      the window is too short, or nobody is being told
 *   accepted     the thing is earning its keep
 *
 * ── AND WHAT IS DELIBERATELY ABSENT ─────────────────────────────────────────
 *
 * There is no button here. No quoting for a pharmacy, no accepting for a buyer,
 * no editing a price — ChiCare is the pharmacy's agent and never the seller
 * (§1.2), and a staff member who could write a quote would be ChiCare pricing a
 * medicine it does not own.
 *
 * And no prescription image. §9.15 keeps the paper with the pharmacist who is
 * dispensing it, with every read logged; "staff can open any prescription from
 * an admin screen" is precisely the casual access that rule exists to stop. So
 * this reports that a pharmacy opened one, and never shows it.
 */
export function QuoteBoardScreen() {
  const [status, setStatus] = useState<string>('live');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'quote-board', status],
    queryFn: ({ signal }) => api.quoteBoard(status, signal),
    refetchInterval: 60_000,
  });

  if (isError) {
    return <AdminError title="We couldn't load the board" error={error} onRetry={() => refetch()} />;
  }

  const requests = data?.requests ?? [];

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Prescriptions</h1>
        <p className="mt-1 max-w-[70ch] text-[13px] leading-relaxed text-muted">
          Buyers sending a prescription to be priced. This is a view, not a desk — pricing belongs
          to the pharmacy, and the prescription itself is only ever read by whoever dispenses it.
        </p>
      </header>

      {data ? (
        <div className="mb-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          <Stat label="Open" value={data.meta.live} />
          {/* The one worth acting on: somebody asked and nobody replied. */}
          <Stat label="Nobody replied" value={data.meta.unanswered} warn={data.meta.unanswered > 0} />
          <Stat label="Accepted" value={data.meta.accepted} />
          <Stat label="Expired unanswered" value={data.meta.expired} warn={data.meta.expired > 0} />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatus(key)}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-medium',
              status === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-[92px] w-full" radius="card" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          title={status === 'live' ? 'Nothing open' : 'Nothing here'}
          body="When a buyer sends a prescription it appears here, with who was asked and who replied."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="min-h-11 rounded-btn border border-line bg-white px-4 text-[13px] font-semibold text-ink"
            >
              Check again
            </button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {requests.map((one) => (
            <BoardRow key={one.reference} request={one} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-card border bg-white px-3.5 py-3',
        warn ? 'border-caution' : 'border-line',
      )}
    >
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">{label}</p>
      <p
        className={cn(
          'm-0 mt-0.5 font-display text-[22px] font-bold tracking-[-0.02em] tabular',
          warn ? 'text-caution-ink' : 'text-ink',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function BoardRow({ request }: { request: QuoteBoardRow }) {
  return (
    <div
      className={cn(
        'border-b border-line px-4 py-3 last:border-b-0',
        request.unanswered && !request.has_expired ? 'bg-caution-tint' : null,
      )}
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-1.5">
        <div className="min-w-[200px] flex-1">
          <p className="m-0 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              {request.reference}
            </span>
            {request.is_veterinary ? (
              <span className="rounded-pill border border-blue px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.12em] text-blue">
                Animal
              </span>
            ) : null}
            {request.unanswered && !request.has_expired ? (
              <Badge tone="caution">Nobody replied</Badge>
            ) : null}
            {request.order_reference ? <Badge tone="verified">Ordered</Badge> : null}
            {request.has_expired && !request.order_reference ? (
              <Badge tone="neutral">Expired</Badge>
            ) : null}
          </p>

          <p className="m-0 mt-0.5 text-[13.5px] font-medium text-ink">
            {request.summary || (request.has_prescription ? 'Photograph only' : '—')}
          </p>

          <p className="m-0 mt-0.5 text-[11.5px] text-faint">
            {[request.buyer, request.delivering_to].filter(Boolean).join(' · ')}
          </p>
        </div>

        <div className="min-w-[140px] text-[12px] text-muted">
          <span className="block">
            {request.replied} of {request.asked} replied
          </span>
          {/* §9.15 as a count. The image itself is never on this screen. */}
          {request.has_prescription ? (
            <span className="block text-[11px] text-faint">
              {request.opened_prescription} opened the prescription
            </span>
          ) : null}
        </div>

        <div className="text-right">
          {request.cheapest_display ? (
            <>
              <span className="block font-mono text-[13px] font-bold text-ink tabular">
                {request.cheapest_display}
              </span>
              {/* The spread is the argument for competing at all. */}
              {request.dearest_display ? (
                <span className="block text-[11px] text-faint">
                  to {request.dearest_display}
                </span>
              ) : null}
            </>
          ) : (
            <span className="block text-[12px] text-faint">No prices yet</span>
          )}

          {request.accepted_from ? (
            <span className="mt-0.5 block text-[11px] text-verified">
              {request.accepted_from}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
